/** Volunteer teacher applications (owner request 2026-10-20): the public form and the administration's list. */
import { z } from "zod";
import { Gender, phone } from "./fields.js";
export const VOLUNTEER_TIMES = ["fajr", "asr", "isha", "other"];
export const VolunteerTime = z.enum(VOLUNTEER_TIMES);
export const VOLUNTEER_EXPERIENCE = ["none", "lt1", "1to3", "gt3"];
export const VolunteerExperience = z.enum(VOLUNTEER_EXPERIENCE);
export const VolunteerStatus = z.enum(["new", "interview", "accepted", "rejected"]);
const digits = (s) => s
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/\s/g, "");
const surah = z.number().int().min(1).max(114);
/** CV files the form accepts, up to CV_MAX_BYTES. */
export const CV_TYPES = {
    "application/pdf": "PDF",
    "application/msword": "Word",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
    "image/jpeg": "صورة",
    "image/png": "صورة",
};
export const CV_MAX_BYTES = 5 * 1024 * 1024;
export const VolunteerCvFile = z.object({
    name: z.string().trim().min(1).max(150),
    type: z.string().refine((t) => Object.hasOwn(CV_TYPES, t), "السيرة الذاتية: PDF أو Word أو صورة"),
    /** The file's bytes in base64. */
    data: z
        .string()
        .regex(/^[A-Za-z0-9+/]+={0,2}$/)
        .refine((d) => Math.floor((d.length * 3) / 4) <= CV_MAX_BYTES, "حجم السيرة الذاتية أكبر من 5 ميجابايت"),
});
export const SubmitVolunteerBody = z
    .object({
    organizationId: z.uuid(),
    name: z
        .string()
        .trim()
        .max(120)
        .refine((v) => v.split(/\s+/).filter(Boolean).length >= 4, "اكتب الاسم رباعيًا"),
    // Residence in Saudi Arabia is not a condition (owner 2026-10-22): an ID, iqama or passport number.
    nationalId: z
        .string()
        .transform((v) => digits(v).toUpperCase())
        .pipe(z.string().regex(/^[A-Z0-9]{5,20}$/, "رقم الهوية أو الإقامة أو الجواز: من 5 إلى 20 حرفًا أو رقمًا")),
    nationality: z.string().trim().min(2).max(40),
    gender: Gender,
    phone,
    email: z.string().trim().toLowerCase().pipe(z.email("البريد الإلكتروني غير صحيح").max(120)),
    city: z.string().trim().max(60).optional(),
    fromSurah: surah,
    toSurah: surah,
    hasIjazah: z.boolean(),
    times: z.array(VolunteerTime).min(1, "اختر وقتًا واحدًا على الأقل").max(4),
    timesOther: z.string().trim().max(120).optional(),
    experience: VolunteerExperience.optional(),
    note: z.string().trim().max(500).optional(),
    /** The CV (optional): a link, or a file (owner 2026-10-22). */
    cvUrl: z
        .url({ protocol: /^https?$/, error: "رابط السيرة الذاتية غير صحيح" })
        .max(500)
        .optional(),
    cv: VolunteerCvFile.optional(),
    /** The applicant read and accepts the conditions. */
    agree: z.literal(true, "يلزم الموافقة على شروط التطوع"),
    /** Honeypot: humans never fill it. */
    website: z.string().max(200).optional(),
})
    .refine((v) => !v.times.includes("other") || !!v.timesOther, {
    message: "اذكر الوقت المناسب لك",
    path: ["timesOther"],
});
export const PublicVolunteerResponse = z.object({
    organizationId: z.uuid(),
    organizationName: z.string(),
    surahs: z.array(z.object({ id: z.number(), name: z.string() })),
});
export const MemorizedQuery = z.object({
    from: z.coerce.number().pipe(surah),
    to: z.coerce.number().pipe(surah),
});
export const MemorizedResponse = z.object({ pages: z.number(), juz: z.number() });
export const VolunteerApplication = z.object({
    id: z.uuid(),
    name: z.string(),
    nationalId: z.string(),
    nationality: z.string(),
    gender: Gender,
    phone: z.string(),
    email: z.string().nullable(),
    city: z.string().nullable(),
    fromSurah: z.number(),
    toSurah: z.number(),
    juz: z.number(),
    hasIjazah: z.boolean(),
    times: z.array(VolunteerTime),
    timesOther: z.string().nullable(),
    experience: VolunteerExperience.nullable(),
    note: z.string().nullable(),
    cvUrl: z.string().nullable(),
    /** A CV file is attached (GET /volunteer-applications/:id/cv). */
    hasCv: z.boolean(),
    status: VolunteerStatus,
    createdAt: z.string(),
    decidedAt: z.string().nullable(),
    decisionNote: z.string().nullable(),
    userId: z.uuid().nullable(),
    /** When the administration last sent the applicant a WhatsApp message from the platform. */
    messagedAt: z.string().nullable(),
    /** The latest interview: its state and time, or until when its booking link works. */
    interview: z
        .object({
        id: z.uuid(),
        status: z.enum(["invited", "booked", "cancelled", "finished", "missed"]),
        startsAt: z.string().nullable(),
        bookBy: z.string(),
    })
        .nullable(),
});
export const VolunteerApplicationsQuery = z.object({ status: VolunteerStatus.optional() });
export const VolunteerApplicationsResponse = z.object({ applications: z.array(VolunteerApplication) });
export const DecideVolunteerBody = z.object({
    status: z.enum(["interview", "accepted", "rejected"]),
    note: z.string().trim().max(500).optional(),
    /** On acceptance: the teacher account created for the applicant. */
    userId: z.uuid().optional(),
});
/** In a message to applicants, this is replaced by each one's name. */
export const VOLUNTEER_NAME_PLACEHOLDER = "{الاسم}";
/** A WhatsApp message from the organization's number to chosen applicants (owner request 2026-11-01). */
export const MessageVolunteersBody = z.object({
    ids: z.array(z.uuid()).min(1).max(100),
    body: z.string().trim().min(1).max(2000),
});
export const MessageVolunteersResponse = z.object({
    results: z.array(z.object({ id: z.uuid(), ok: z.boolean(), error: z.string().nullable() })),
});
/** The form's lists and labels, shared by the portal's page and the app's form. */
export const VOLUNTEER_CONDITIONS = [
    "أن تكون حافظًا للقرآن الكريم، متقنًا لتلاوته.",
    "أن تلتزم بالمواعيد والمهام التعليمية المتفق عليها.",
    "أن تجتاز المقابلة الشخصية.",
];
export const VOLUNTEER_NATIONALITIES = [
    "سعودي",
    "مصري",
    "سوداني",
    "يمني",
    "سوري",
    "أردني",
    "فلسطيني",
    "باكستاني",
    "هندي",
    "بنغلاديشي",
    "إندونيسي",
    "نيجيري",
    "تشادي",
    "موريتاني",
    "صومالي",
    "إثيوبي",
    "أخرى",
];
export const volunteerTimeLabel = {
    fajr: "الفترة الصباحية (بعد صلاة الفجر)",
    asr: "الفترة المسائية (بعد صلاة العصر)",
    isha: "الفترة المسائية (بعد صلاة العشاء)",
    other: "وقت آخر",
};
export const volunteerExperienceLabel = {
    none: "لا خبرة سابقة",
    lt1: "أقل من سنة",
    "1to3": "من سنة إلى ثلاث سنوات",
    gt3: "أكثر من ثلاث سنوات",
};
//# sourceMappingURL=volunteers.js.map